import * as React from 'react';
import {
  Outlet,
  ScrollRestoration,
  createRootRouteWithContext,
} from '@tanstack/react-router';
import { HeadContent, Scripts, type ErrorComponentProps } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { VapiButton } from '@/components/vapi/VapiButton';
import { ConsentBanner } from '@/components/analytics/ConsentBanner';
import { PostHogProvider } from '@/components/analytics/PostHogProvider';
import { FloatingAiControls } from '@/components/FloatingAiControls';
import { getTenantContext } from '@/lib/tenant/tenant-context.functions';
import { TenantProvider } from '@/lib/tenant/useTenant';
import { isRequestAbortError } from '@/lib/request-abort';
import { SITE_URL } from '@/lib/seo/site';
import '@/styles.css';

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Poonam Kapur Healthy Kitchen — Healthy Meals in Mumbai',
      },
      { property: 'og:site_name', content: 'Poonam Kapur Healthy Kitchen' },
      { property: 'og:type', content: 'website' },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              '@id': `${SITE_URL}/#organization`,
              name: 'Poonam Kapur Healthy Kitchen',
              url: `${SITE_URL}/`,
            },
            {
              '@type': 'WebSite',
              '@id': `${SITE_URL}/#website`,
              name: 'Poonam Kapur Healthy Kitchen',
              url: `${SITE_URL}/`,
              publisher: { '@id': `${SITE_URL}/#organization` },
            },
          ],
        }),
      },
    ],
  }),
  // Tenant is resolved server-side from the request host only. Fail-safe:
  // any error keeps the existing (default tenant) behaviour untouched.
  loader: async () => {
    try {
      return { tenant: await getTenantContext() };
    } catch {
      return { tenant: null };
    }
  },
  component: RootComponent,
  errorComponent: ErrorComponent,
  notFoundComponent: NotFoundComponent,
});

function RootComponent() {
  const context = Route.useRouteContext();
  const loaderData = Route.useLoaderData() as
    | { tenant: import('@/lib/tenant/types').TenantContext | null }
    | undefined;

  return (
    <QueryClientProvider client={context.queryClient}>
      <TenantProvider value={loaderData?.tenant ?? null}>

      <html lang="en">
        <head>
          <HeadContent />
          {/* Preconnect for performance */}
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
          
          {/* Fonts - instrument-serif-work-sans */}
          <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Work+Sans:ital,wght@0,100..900;1,100..900&display=swap" rel="stylesheet" />
        </head>
        <body className="font-sans antialiased selection:bg-primary/10">
          <Outlet />
          <ScrollRestoration />
          <Scripts />
          
          {/*   is globally disabled via its own component return null, but removed here for absolute safety */}
          <FloatingAiControls />
          <ConsentBanner />
          <PostHogProvider />
          <Toaster richColors position="top-center" />
          {/* VapiButton is now handled within FloatingAiControls */}
          
          <React.Suspense fallback={null}>
            <div className="hidden" aria-hidden="true" id="e2e-instruction-overlay">
              {/* E2E instruction hidden to prevent interaction blocking */}
            </div>
          </React.Suspense>
        </body>
      </html>
      </TenantProvider>
    </QueryClientProvider>
  );
}

function ErrorComponent({ error }: ErrorComponentProps) {
  // A cancelled request (refresh, navigating away) is not a failure — show the
  // normal loading state instead of an error screen.
  if (isRequestAbortError(error)) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="p-4 text-red-600 bg-red-50 border border-red-200 rounded">
      <h2 className="text-lg font-bold">Something went wrong</h2>
      <pre className="text-sm mt-2">{(error as Error | undefined)?.message}</pre>
      <button 
        onClick={() => window.location.reload()}
        className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
      >
        Reload page
      </button>
    </div>
  );
}

function NotFoundComponent() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] p-4">
      <h1 className="text-4xl font-bold text-gray-900">404</h1>
      <p className="text-lg text-gray-600 mt-2">Page not found</p>
      <a 
        href="/"
        className="mt-6 px-6 py-3 bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-opacity"
      >
        Go Home
      </a>
    </div>
  );
}